import { useEffect, useRef } from "react";
import { API_BASE } from "../config/api";
import {
  getDistributorToken,
  getClientToken,
  handleDistributorAuthFailure,
  handleClientAuthFailure,
} from "../config/auth";

const IDLE_MS = 15 * 60 * 1000;
const HEARTBEAT_MS = 4 * 60 * 1000;

export default function SessionActivityMonitor({ realm }) {
  const inactiveSinceRef = useRef(null);
  const heartbeatBusyRef = useRef(false);

  useEffect(() => {
    const isClient = realm === "client";
    const getToken = isClient ? getClientToken : getDistributorToken;
    const handleAuthFailure = isClient
      ? handleClientAuthFailure
      : handleDistributorAuthFailure;
    const endpoint = isClient
      ? "client_session_touch.php"
      : "distributor_session_touch.php";
    const headerName = isClient
      ? "X-KG-Client-Authorization"
      : "X-KG-Distributor-Authorization";

    const isActive = () =>
      document.visibilityState === "visible" &&
      (typeof document.hasFocus !== "function" || document.hasFocus());

    const heartbeat = async ({ force = false } = {}) => {
      if (heartbeatBusyRef.current) return;
      if (!force && !isActive()) return;

      const token = getToken();
      if (!token) return;

      heartbeatBusyRef.current = true;
      try {
        const response = await fetch(`${API_BASE}/${endpoint}`, {
          method: "GET",
          headers: {
            [headerName]: `Bearer ${token}`,
          },
          cache: "no-store",
        });
        const data = await response.json().catch(() => ({}));
        handleAuthFailure(response, data);
      } catch (_) {
        // Do not log out only because the network is temporarily unavailable.
        // The server remains authoritative when connectivity returns.
      } finally {
        heartbeatBusyRef.current = false;
      }
    };

    const markInactive = () => {
      if (inactiveSinceRef.current === null) {
        inactiveSinceRef.current = Date.now();
      }
    };

    const resumeActive = () => {
      if (!isActive()) return;

      const inactiveSince = inactiveSinceRef.current;
      inactiveSinceRef.current = null;

      // If the app/tab/window was inactive for 15+ minutes, do not refresh the
      // session first. Force one server check; the server will reject/delete
      // the idle token and the normal auth-failure handler returns to login.
      if (inactiveSince !== null && Date.now() - inactiveSince >= IDLE_MS) {
        heartbeat({ force: true });
        return;
      }

      heartbeat();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        resumeActive();
      } else {
        markInactive();
      }
    };

    const onFocus = () => resumeActive();
    const onBlur = () => markInactive();

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);

    // A protected layout means the user is already authenticated. Keep the
    // session alive while this app/page is actually visible and focused.
    if (isActive()) {
      inactiveSinceRef.current = null;
      heartbeat();
    } else {
      markInactive();
    }

    const timer = window.setInterval(() => {
      if (isActive()) heartbeat();
    }, HEARTBEAT_MS);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
    };
  }, [realm]);

  return null;
}
